<template>
  <div id="accordionEmailLogs" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingEmailLogs" class="accordion-header">
        <button
          aria-controls="collapseEmailLogs"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseEmailLogs"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="description" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">Email Logs</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">
                All outgoing emails (clients &amp; suppliers)
              </div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseEmailLogs"
        aria-labelledby="headingEmailLogs"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionEmailLogs"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <div class="d-flex align-items-center gap-8 mb-12">
            <select v-model="filterType" class="form-select form-select-sm" style="width: auto;" @change="loadLogs">
              <option value="">All Types</option>
              <option value="client">Client</option>
              <option value="supplier">Supplier</option>
            </select>

            <v-button size="xs" variant="primary" :disabled="loading" @click="loadLogs">
              <v-icon icon="refresh" size="xs" space="me-4" />
              Refresh
            </v-button>
          </div>

          <v-loader v-if="loading" />

          <div v-else class="table-responsive text-xs">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Email Type</th>
                  <th>Case</th>
                  <th>Sent At</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="logs.length === 0">
                  <td colspan="6" class="text-center text-muted italic py-16">
                    No email logs found.
                  </td>
                </tr>
                <tr v-for="log in logs" :key="log.id">
                  <td class="text-nowrap">{{ log.email_to }}</td>
                  <td>{{ log.subject || '—' }}</td>
                  <td>
                    <span :class="log.type === 'client' ? 'badge bg-primary' : 'badge bg-info'">
                      {{ log.type }}
                    </span>
                  </td>
                  <td class="text-nowrap">{{ log.email_type }}</td>
                  <td class="text-nowrap">
                    <span v-if="log.case">{{ log.case.number || log.case.id }}</span>
                    <span v-else>—</span>
                  </td>
                  <td class="text-nowrap">{{ formatDate(log.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="pagination.last_page > 1" class="d-flex justify-content-center mt-12">
            <button
              class="btn btn-sm btn-outline-primary me-4"
              :disabled="!pagination.prev_page_url"
              @click="changePage(pagination.current_page - 1)"
            >
              Prev
            </button>
            <span class="align-self-center text-xs">
              Page {{ pagination.current_page }} of {{ pagination.last_page }}
            </span>
            <button
              class="btn btn-sm btn-outline-primary ms-4"
              :disabled="!pagination.next_page_url"
              @click="changePage(pagination.current_page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  agency?: string;
  caseId?: string | number;
}>();

const route = useRoute();
const agency = props.agency || (route.params.agency as string) || "";

const loading = ref(false);
const logs = ref<any[]>([]);
const filterType = ref("");
const pagination = ref<any>({
  current_page: 1,
  last_page: 1,
  prev_page_url: null,
  next_page_url: null,
});

const formatDate = (dateStr: string) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-GB");
};

const loadLogs = async (page = 1) => {
  loading.value = true;
  try {
    const params: any = { page };
    if (filterType.value) {
      params.type = filterType.value;
    }
    if (props.caseId) {
      params.case_id = props.caseId;
    }
    const res = await api.case.emailLogs(agency, params);
    const paginated = res.data || res;
    logs.value = paginated.data || [];
    pagination.value = {
      current_page: paginated.current_page || 1,
      last_page: paginated.last_page || 1,
      prev_page_url: paginated.prev_page_url || null,
      next_page_url: paginated.next_page_url || null,
    };
  } catch (e: any) {
    console.error("Failed to load email logs", e);
  } finally {
    loading.value = false;
  }
};

const changePage = (page: number) => {
  if (page < 1 || page > pagination.value.last_page) return;
  loadLogs(page);
};

onMounted(() => {
  loadLogs();
});
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

.table-responsive {
  overflow: auto;
  max-height: 30rem;

  thead th {
    background-color: var(--uq-body-bg);
    position: sticky;
    top: 0;
    z-index: 1;
  }
}
</style>
